import { useState } from "react";
import {
  Button,
  Dialog,
  FormField,
  Select,
  Textarea,
  useToast,
} from "@chotca/ui";
import type { OrgVerifyLevel } from "@chotca/config";
import { useOverrideOrgVerifyLevel } from "@chotca/api-client";

const LEVELS: { value: OrgVerifyLevel; label: string }[] = [
  { value: "L0_NEW", label: "L0 — Mới" },
  { value: "L1_CONTACT", label: "L1 — Đã xác minh liên hệ" },
  { value: "L2_DOCUMENT", label: "L2 — Đã xác minh giấy tờ" },
  { value: "L3_TRUSTED", label: "L3 — Nhà tuyển dụng uy tín" },
];

// ADMIN override of the computed verifyLevel (SCRUM-74). Separate from the org
// edit form so the computed path stays untouched. Reason is mandatory; the API
// audit-logs the change.
export function OverrideVerifyLevel({
  orgId,
  current,
}: {
  orgId: string;
  current: OrgVerifyLevel;
}) {
  const { toast } = useToast();
  const override = useOverrideOrgVerifyLevel();
  const [open, setOpen] = useState(false);
  const [level, setLevel] = useState<OrgVerifyLevel>(current);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!reason.trim()) {
      setError("Bắt buộc nhập lý do.");
      return;
    }
    try {
      await override.mutateAsync({
        orgId,
        body: { level, reason: reason.trim() },
      });
      toast({ tone: "success", title: "Đã ghi đè mức xác thực" });
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Ghi đè thất bại");
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setLevel(current);
          setReason("");
          setError(null);
          setOpen(true);
        }}
      >
        Ghi đè mức xác thực
      </Button>
      <Dialog
        open={open}
        onClose={() => {
          if (!override.isPending) setOpen(false);
        }}
        title="Ghi đè mức xác thực (ADMIN)"
        description="Ghi đè mức hệ thống tự tính. Bắt buộc nhập lý do — thao tác được ghi audit log."
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={override.isPending}
            >
              Huỷ
            </Button>
            <Button onClick={submit} loading={override.isPending}>
              Xác nhận
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <FormField label="Mức xác thực">
            <Select
              value={level}
              onValueChange={(v) => setLevel(v as OrgVerifyLevel)}
              options={LEVELS}
            />
          </FormField>
          <FormField label="Lý do (bắt buộc)">
            <Textarea
              rows={3}
              maxLength={500}
              placeholder="VD: xác minh thủ công qua giấy tờ gửi email…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </FormField>
          {error ? <p className="text-xs text-ember">{error}</p> : null}
        </div>
      </Dialog>
    </>
  );
}
